






























import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import { IProvisionSetupFunction, IBlueprint } from "@/models/provisioning";
import { IProduct } from "@/models/products";

@Component({
  components: {
    "configure-field-row": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/configureFieldRow.vue"
      )
  },
  computed: {
    ...mapState({
      setupFunctionsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.PROVISIONING}/productSetupFunctionsScope`
        ](this.productId);
      },
      setupFunctions(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING,
          scope: this.setupFunctionsScope
        });
      },

      blueprintScope(state: IState, getters) {
        return getters[`data/${DataModules.PROVISIONING_BLUEPRINTS}/scope`](
          this.blueprintId
        );
      },
      blueprint(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.PROVISIONING_BLUEPRINTS,
          scope: this.blueprintScope
        });
      }
    })
  }
})
export default class ConfigureSetupFunctionsList extends Mixins(
  RequestPromises
) {
  public setupFunctionsScope!: string;
  public setupFunctions!: IProvisionSetupFunction[];
  public blueprint!: IBlueprint;
  public blueprintScope!: string;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public blueprintId!: IBlueprint["id"];

  @Prop({ default: false })
  public isDisabled!: boolean;

  public guardFunctionalities = ["list_product_provision_setup_functions"];

  public isLoading = false;
  public isReloading = false;
  public requestError = false;

  public created() {
    if (!this.isContentAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData(ignoreStored = false) {
    try {
      await Promise.all([
        this.getProductSetupFunctions(ignoreStored),
        this.getBlueprintData(ignoreStored)
      ]);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData(this.isReloading);
  }

  public getProductSetupFunctions(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING}/listProductSetupFunctions`,
      {
        id: this.productId,
        scope: this.setupFunctionsScope,
        vm: this,
        params: {
          order: "order",
          limit: 0,
          with: "blueprint_function"
        },
        ignoreStored
      }
    );
  }

  public getBlueprintData(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BLUEPRINTS}/get`,
      {
        id: this.blueprintId,
        scope: this.blueprintScope,
        vm: this,
        params: {
          with: "setup_functions,setup_functions.blueprint_function"
        },

        ignoreStored
      }
    );
  }

  public get setupFunctionsLabels() {
    return this.$_.uniq(
      this.$_.concat(
        this.defaultFunctionNames,
        this.$_.map(this.setupFunctions, setupFunction => {
          return this.$_.get(
            setupFunction,
            "blueprint_function.label",
            setupFunction.blueprint_function_id === "none"
              ? this.$t("_.none")
              : this.defaultFunctionNames
          );
        })
      )
    ).join(" | ");
  }

  public get defaultFunctionNames() {
    return this.$_.map(
      this.$_.filter(
        this.$_.get(this.blueprint, "setup_functions", []),
        setupFunction => {
          return !this.$_.find(this.setupFunctions, [
            "blueprint_function_id",
            setupFunction.blueprint_function_id
          ]);
        }
      ),
      filteredFunction => {
        return `${filteredFunction.blueprint_function.label} (${this.$t(
          "_._default"
        )})`;
      }
    );
  }

  public async configureField() {
    if (this.isDisabled) return;

    this.$store.dispatch(
      `data/${DataModules.PROVISIONING}/openManageSetupFunctionsModal`,
      {
        props: {
          productId: this.productId,
          blueprintId: this.blueprintId,
          setupFunctions: this.setupFunctions
        },
        events: {
          success: () => {
            this.isLoading = true;
            this.loadData(true);
          }
        }
      }
    );
  }

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING,
      scope: this.setupFunctionsScope,
      vm: this
    });

    this.$store.dispatch("data/bin", {
      storeModule: DataModules.PROVISIONING_BLUEPRINTS,
      scope: this.blueprintScope,
      vm: this
    });
  }
}
